"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Table, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { PATHNAME } from "../../../pathname/index";
import {
  getAllUnits,
  getExecutionReport,
} from "../../../redux/unitSlice/unitSlice";
import CustomSelectWithFilter from "../../../components/Common/CustomSelect/CustomSelectWithFilter";
import ActiveProject from "../../../assets/icons/Region.svg";
import { useNavigate } from "react-router-dom";
import { getOtherProjectsDate } from "../../../redux/otherProjectsSlice/otherProjectsSlice";

const { Option } = Select;

/* ===================== CONSTANTS ===================== */
const YEAR_TOTAL_SUFFIX = "__YEAR_TOTAL";

const headerStyle = {
  background: "#008dd3",
  color: "#fff",
  fontWeight: 600,
  textAlign: "center",
};

const MONTH_ORDER = [
  "Apr", "May", "Jun",
  "Jul", "Aug", "Sep",
  "Oct", "Nov", "Dec",
  "Jan", "Feb", "Mar",
];

/* ===================== UTILS ===================== */
const getFYShortLabel = (year) => {
  const y = Number(year);
  const next = y + 1;

  return `${String(y).slice(-2)}-${String(next).slice(-2)}`;
};

const getQ4Value = (periods) => {
  const q4Key = getPreviousFYQ4Key();

  // Quarter exists
  if (periods[q4Key] !== undefined) {
    return toNumber(periods[q4Key]);
  }

  // Month fallback
  const fyYear = Number(q4Key.split("-")[1]) + 1;

  return (
    toNumber(periods[`Jan-${fyYear}`]) +
    toNumber(periods[`Feb-${fyYear}`]) +
    toNumber(periods[`Mar-${fyYear}`])
  );
};

const getCurrentFYQ4Key = () => {
  const today = new Date();

  // FY starts in April
  const fyStartYear =
    today.getMonth() >= 3
      ? today.getFullYear()
      : today.getFullYear() - 1;

  return `Q4-${fyStartYear - 1}`;
};

const getCurrentFYStartYear = () => {
  const today = new Date();

  return today.getMonth() >= 3
    ? today.getFullYear()   // Apr-Dec
    : today.getFullYear() - 1; // Jan-Mar
};

const getBalanceTitle = () => {
  const today = new Date();
  const year = today.getFullYear() - 1; // previous year

  const shortYear = String(year).slice(-2);

  return `Order O/s as on 31-12-${shortYear}`;
};

const getAprilBalanceTitle = () => {
  const year = new Date().getFullYear(); // current year
  const shortYear = String(year).slice(-2);

  return `Balance as on 01-04-${shortYear}`;
};
/* ===================== BASE COLUMNS ===================== */

const baseColumn = {
  title: "Orders",
  dataIndex: "label",
  fixed: "left",
  width: 460,
  className: "orders-column",
  render: (text) => <strong>{text}</strong>,
  onHeaderCell: () => ({ style: headerStyle }),
};

const balanceRevenueColumn = {
  // title: "Order Outstanding as per Dec",
  title: getBalanceTitle(),
  dataIndex: "BALANCE_REVENUE",
  width: 120,
  align: "center",
  render: (val) => {
    // Handle "-" values from API
    if (val === 0 && val !== undefined) return "0";
    if (val === 0 || val === undefined || val === null) return "-";
    return formatNumber(val);
  },
  onHeaderCell: () => ({ style: headerStyle }),
};

const balanceRevenueDifferenceColumn = {
  // title: "Balance as on April",
  title: getAprilBalanceTitle(),
  dataIndex: "BALANCE_REVENUE_DIFF",
  width: 120,
  align: "center",
  render: (val) => {
    if (val === 0 || val === undefined || val === null) return "-";
    return formatNumber(val);
  },
  onHeaderCell: () => ({ style: headerStyle }),
};

const overallColumn = {
  title: "Overall Total",
  dataIndex: "ROW_OVERALL_TOTAL",
  fixed: "right",
  width: 100,
  align: "center",
  className: "total-column",
  render: (val) => formatNumber(val),
  onHeaderCell: () => ({ style: headerStyle }),
};



const buildYearTotalColumn = (year) => ({
  title: "Total",
  dataIndex: `${year}${YEAR_TOTAL_SUFFIX}`,
  width: 140,
  align: "center",
  render: (val) => formatNumber(val),
  onHeaderCell: () => ({
    style: {
      background: "#006da4",
      color: "#fff",
      fontWeight: 600,
    },
  }),
});

const getPreviousFYQ4Key = () => {
  const today = new Date();

  const currentFYStart =
    today.getMonth() >= 3
      ? today.getFullYear()
      : today.getFullYear() - 1;

  return `Q4-${currentFYStart - 1}`;
};

/* ===================== NORMALIZE ===================== */
/* ----------------- CONVERT VALUE TO NUMBER ----------------- */
export const toNumber = (v) =>
  v ? Number(String(v).replace(/,/g, "")) || 0 : 0;

const isMonthKey = (key) =>
  /^[A-Z][a-z]{2}-\d{4}$/.test(key) ||   // Apr-2026
  /^[A-Z][a-z]{2}_\d{2}$/.test(key);     // Apr_26

const isQuarterKey = (key) =>
  /^Q[1-4]-\d{4}$/.test(key) ||        // legacy: Q1-2026
  /^Q[1-4]-\d{4}-\d{4}$/.test(key);

const isYearKey = (key) =>
  /^\d{4}-\d{4}$/.test(key); // 2025-2026

/* ----------------- GET YEAR FROM KEY ----------------- */
export const getYearFromKey = (key, viewType) => {
  if (/^\d{4}-\d{4}$/.test(key)) {
    return viewType === "Year" ? key : key.split("-")[0];
  }

  // NEW: Q1-2026-2027 → year group is "2026-2027"
  if (/^Q[1-4]-\d{4}-\d{4}$/.test(key)) {
    return `${key.split("-")[1]}-${key.split("-")[2]}`;
  }

  // legacy quarter: Q1-2026 → "2026"
  if (/^Q[1-4]-\d{4}$/.test(key)) {
    return key.split("-")[1];
  }

  if (/^[A-Za-z]{3}-\d{4}$/.test(key)) {
    const [month, year] = key.split("-");
    const y = Number(year);
    if (["Jan", "Feb", "Mar"].includes(month)) {
      return `${y - 1}-${y}`;
    }
    return `${y}-${y + 1}`;
  }

  if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
    const shortYear = Number(key.split("_")[1]);
    return `20${shortYear}`;
  }

  return null;
};

/* ----------------- FORMAT NUMBER ----------------- */
const nf2 = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const headerFYLabel = (yearKey) => {
  // Month grouping gives "2024", "2025" etc → show "2024-2025"
  if (/^\d{4}$/.test(yearKey)) {
    const y = Number(yearKey);
    return `${y}-${y + 1}`;
  }

  // Quarter/Year already gives "2024-2025" → keep as-is
  if (/^\d{4}-\d{4}$/.test(yearKey)) return yearKey;

  return yearKey;
};

export const formatNumber = (val) => {
  const num = toNumber(val);
  // keep "-" for truly missing/undefined/null; show 0.00 for 0
  if (val === undefined || val === null || val === "") return "-";
  if (num === 0) return "0";
  return nf2.format(num);
};

export const normalizePeriodsOnly = (data = {}, viewType, isAnticipated = false) => {
  const result = {};

  Object.entries(data)?.forEach(([key, value]) => {
    let normalizedKey = key;

    if (isAnticipated) {
      if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
        const [month, yy] = key.split("_");
        normalizedKey = `${month}-20${yy}`;
      }
    }

    // ✅ Prefer "<year>_formatted" sibling for Year view — raw year totals
    // in direct_orders/anticipated_orders are scaled 100x vs the formatted value.
    const sourceValue =
      viewType === "Year" && isYearKey(normalizedKey) && data[`${key}_formatted`] !== undefined
        ? data[`${key}_formatted`]
        : value;

    const num = toNumber(sourceValue);
    if (Number.isNaN(num) || num === undefined || num === null) return;

    // MONTH -> QUARTER CONVERSION
    // Only derive a quarter from months when the source has no literal quarter
    // keys of its own (anticipated_orders). Clear/hold/other-projects data
    // already carries an authoritative "Q1-2026"-style key alongside its month
    // keys — deriving one here too would make both versions collide on the
    // same result key and overwrite each other in object-iteration order.
    if (isAnticipated && viewType === "Quarter" && isMonthKey(normalizedKey)) {
      const [month, yearStr] = normalizedKey.split("-");
      const year = Number(yearStr);

      let quarter = "";
      let fyStartYear;

      if (["Apr", "May", "Jun"].includes(month)) { quarter = "Q1"; fyStartYear = year; }
      else if (["Jul", "Aug", "Sep"].includes(month)) { quarter = "Q2"; fyStartYear = year; }
      else if (["Oct", "Nov", "Dec"].includes(month)) { quarter = "Q3"; fyStartYear = year; }
      else if (["Jan", "Feb", "Mar"].includes(month)) { quarter = "Q4"; fyStartYear = year - 1; }

      normalizedKey = `${quarter}-${fyStartYear}`;
    }

    // APPLY FILTERS
    if (viewType === "Month" && !isMonthKey(normalizedKey)) return;
    if (viewType === "Quarter" && !isQuarterKey(normalizedKey)) return;
    if (viewType === "Year" && !isYearKey(normalizedKey)) return;

    if (viewType === "Quarter" && isAnticipated) {
      result[normalizedKey] = (result[normalizedKey] || 0) + num;
    } else {
      result[normalizedKey] = num;
    }
  });

  return result;
};

const normalizeData = (data, viewType, isAnticipated = false) => {
  const result = {};
  const yearTotals = {};
  if (!data) return result;

  Object.entries(data).forEach(([key, value]) => {
    let normalizedKey = key;

    // Convert Apr_26 → Apr-2026 (FY-aware)
    if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
      const [month, yy] = key.split("_");
      // Do NOT adjust year here — keep as calendar year
      // Jan_27 → Jan-2027, Mar_27 → Mar-2027
      normalizedKey = `${month}-20${yy}`;
    }

    if (
      ["PROJ_NAME", "IO_NO", "REV_NO", "description", "upto", "Total"].includes(key)
    ) return;

    const num = toNumber(value);
    if (num === undefined || num === null) return;

    // MONTH -> QUARTER CONVERSION for anticipated
    // if (viewType === "Quarter" && isMonthKey(normalizedKey)) {
    if (
      isAnticipated &&
      viewType === "Quarter" &&
      isMonthKey(normalizedKey)
    ) {
      const [month, yearStr] = normalizedKey.split("-");
      const year = Number(yearStr);

      let quarter = "";
      let fyStartYear;

      if (["Apr", "May", "Jun"].includes(month)) { quarter = "Q1"; fyStartYear = year; }
      else if (["Jul", "Aug", "Sep"].includes(month)) { quarter = "Q2"; fyStartYear = year; }
      else if (["Oct", "Nov", "Dec"].includes(month)) { quarter = "Q3"; fyStartYear = year; }
      else if (["Jan", "Feb", "Mar"].includes(month)) { quarter = "Q4"; fyStartYear = year - 1; }

      normalizedKey = `${quarter}-${fyStartYear}`;
    }

    if (viewType === "Month" && !isMonthKey(normalizedKey)) return;
    if (viewType === "Quarter" && !isQuarterKey(normalizedKey)) return;
    if (viewType === "Year" && !isYearKey(normalizedKey)) return;

    const yearGroup = getYearFromKey(normalizedKey, viewType);

    if (yearGroup) {
      const startYear =
        yearGroup.includes("-")
          ? Number(yearGroup.split("-")[0])
          : Number(yearGroup);

      if (startYear < getCurrentFYStartYear() - 1) {
        return;
      }
    }
    if (viewType === "Quarter" && isAnticipated) {
      result[normalizedKey] =
        (result[normalizedKey] || 0) + num;
    } else {
      result[normalizedKey] = num;
    }

    if (viewType === "Quarter") {
      yearTotals[normalizedKey.split("-")[1]] =
        (yearTotals[normalizedKey.split("-")[1]] || 0) + num;
    }
    if (viewType === "Month") {
      const fyYear = getYearFromKey(normalizedKey, viewType);
      yearTotals[fyYear] = (yearTotals[fyYear] || 0) + num;
    }
  });

  // At the bottom of normalizeData, replace the ROW_OVERALL_TOTAL line:

  const currentFYStartYear = getCurrentFYStartYear(); // 2026

  result.ROW_OVERALL_TOTAL = Object.entries(result)
    .filter(([k]) => {
      // Always exclude suffix and self
      if (k.endsWith(YEAR_TOTAL_SUFFIX)) return false;
      if (k === "ROW_OVERALL_TOTAL") return false;

      const year = getYearFromKey(k, viewType);

      // ✅ If year can't be parsed, still include the value (don't silently drop)
      if (!year || year === "null") return true;

      const yearStart = /^\d{4}-\d{4}$/.test(year)
        ? Number(year.split("-")[0])
        : Number(year);

      // ✅ Only include current FY and beyond
      return yearStart >= currentFYStartYear;
    })
    .reduce((sum, [, v]) => sum + v, 0);

  Object.entries(yearTotals).forEach(([y, v]) => {
    result[`${y}${YEAR_TOTAL_SUFFIX}`] =
      viewType === "Year" && data?.TOTAL_SUM != null
        ? toNumber(data.TOTAL_SUM)
        : v;
  });

  return result;
};

const normalizeData1 = (data, viewType) => {
  const result = {};
  const yearTotals = {};
  if (!data) return result;

  Object.entries(data).forEach(([key, value]) => {
    let normalizedKey = key;

    // Convert Apr_26 → Apr-2026
    if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
      const [month, yy] = key.split("_");
      normalizedKey = `${month}-20${yy}`;
    }
    // skip meta & non-numeric
    if (
      [
        "PROJ_NAME",
        "IO_NO",
        "REV_NO",
        "description",
        "upto",
        "Total",
      ].includes(key)
    ) {
      return;
    }
    if (viewType === "Month" && !isMonthKey(normalizedKey)) return;
    if (viewType === "Quarter" && !isQuarterKey(normalizedKey)) return;
    if (viewType === "Year" && !isYearKey(normalizedKey)) return;
    // const num = toNumber(value);
    // if (num === undefined || num === null) return;

    const sourceValue =
      viewType === "Year" && isYearKey(normalizedKey) && data[`${key}_formatted`] !== undefined
        ? data[`${key}_formatted`]
        : value;

    const num = toNumber(sourceValue);
    if (num === undefined || num === null) return;

    // ---------------- VIEW TYPE FILTER ----------------
    // console.log("normalizing", normalizedKey);
    // const year = getYearFromKey(key, viewType);
    const year = getYearFromKey(normalizedKey, viewType);
    // normal cell

    const yearGroup = getYearFromKey(normalizedKey, viewType);

    if (yearGroup) {
      const startYear =
        yearGroup.includes("-")
          ? Number(yearGroup.split("-")[0])
          : Number(yearGroup);

      if (startYear < getCurrentFYStartYear() - 1) {
        return;
      }
    }
    result[normalizedKey] = num;

    yearTotals[year] = (yearTotals[year] || 0) + num;
    // console.log("year", year);

  });

  // At the bottom of normalizeData, replace the ROW_OVERALL_TOTAL line:

  const currentFYStartYear = getCurrentFYStartYear(); // 2026

  result.ROW_OVERALL_TOTAL = Object.entries(result)
    .filter(([k]) => {
      // Always exclude suffix and self
      if (k.endsWith(YEAR_TOTAL_SUFFIX)) return false;
      if (k === "ROW_OVERALL_TOTAL") return false;

      const year = getYearFromKey(k, viewType);

      // ✅ If year can't be parsed, still include the value (don't silently drop)
      if (!year || year === "null") return true;

      const yearStart = /^\d{4}-\d{4}$/.test(year)
        ? Number(year.split("-")[0])
        : Number(year);

      // ✅ Only include current FY and beyond
      return yearStart >= currentFYStartYear;
    })
    .reduce((sum, [, v]) => sum + v, 0);
  // ---------------- OVERALL TOTAL ----------------
  // result.ROW_OVERALL_TOTAL =
  //   data?.Total != null ? toNumber(data.Total) : calculatedOverall;
  Object.entries(yearTotals).forEach(([y, v]) => {
    result[`${y}${YEAR_TOTAL_SUFFIX}`] =
      viewType === "Year" && data?.TOTAL_SUM != null
        ? toNumber(data.TOTAL_SUM)
        : v;
  });
  return result;
};


export const buildProjectRow = (project, viewType) => {
  const norm = normalizeData(project, viewType);
  const row = {
    key: `PROJ_${project.IO_NO}`,
    label: project.PROJ_NAME,
    BALANCE_REVENUE: project.BALANCE_REVENUE === "-" ? "-" : toNumber(project.BALANCE_REVENUE),
  };
  Object.entries(norm)?.forEach(([k, v]) => {
    row[k] = v;
  });
  return row;
};

const buildBhelOtherProjectsRow = (units = [], viewType) => {
  const combined = {};

  units?.forEach((u) => {
    const data = u.other_projects;
    if (!data) return;

    const norm = normalizeData1(data, viewType);

    Object.entries(norm).forEach(([k, v]) => {
      if (k.endsWith("__YEAR_TOTAL")) return;
      if (k === "ROW_OVERALL_TOTAL") return;

      combined[k] = (combined[k] || 0) + v;
    });
  });

  const row = buildSummaryRow(
    "Other Projects",
    combined,
    viewType
  );

  // ✅ Add Balance Revenue manually
  row.BALANCE_REVENUE = units.reduce(
    (sum, u) =>
      sum + toNumber(u?.other_projects?.BALANCE_REVENUE || 0),
    0
  );

  // ✅ Q4 value
  const q4Key = getPreviousFYQ4Key();

  const q4Total = units.reduce((sum, u) => {
    const rawPeriods = normalizePeriodsOnly(
      u?.other_projects || {},
      "Quarter"
    );

    return sum + getQ4Value(rawPeriods);
  }, 0);

  // ✅ Balance diff column
  row.BALANCE_REVENUE_DIFF =
    row.BALANCE_REVENUE - q4Total;

  row.isOtherProjects = true;

  return row;
};

const buildHoldOrderRow = (units = [], viewType) => {
  const holdCombinedPeriods = {};

  const sectorMap = {}; // Use sector name as key for uniqueness

  units.forEach((unit) => {
    // Process hold_orders for this unit
    (unit.hold_orders || []).forEach((sector) => {
      const sectorKey = sector.BS_UNIT;
      const sectorName = sector.BS_UNIT_NAME;

      // Create or get existing sector (merge across units)
      if (!sectorMap[sectorKey]) {
        sectorMap[sectorKey] = {
          // Namespaced with HOLD_ — clear_orders sectors reuse the same
          // BS_UNIT codes, so an unprefixed key here would collide with
          // buildClearOrderRow's sector keys and corrupt the table's
          // expand/collapse state (rowKey="key" tracks both branches as one).
          key: `HOLD_SECTOR_${sectorKey}`,
          label: sectorName,
          isSubtotal: true,
          _periods: {},
          projects: []
        };
      }

      // Process projects within this sector
      (sector.data || []).forEach((proj) => {
        const projectKey = proj.PROJ_NAME;

        // Check if project already exists in this sector
        if (!sectorMap[sectorKey]._projectMap) {
          sectorMap[sectorKey]._projectMap = {};
        }

        if (!sectorMap[sectorKey]._projectMap[projectKey]) {
          sectorMap[sectorKey]._projectMap[projectKey] = {
            key: `HOLD_PROJ_${sectorKey}_${projectKey}`,
            label: `${proj.PROJ_NAME} (${proj.IO_NO})`,
            _periods: {},
            BALANCE_REVENUE: proj.BALANCE_REVENUE === "-" ? 0 : toNumber(proj.BALANCE_REVENUE),
          };
        } else {
          // Aggregate balance revenue if project already exists
          const currentValue = proj.BALANCE_REVENUE === "-" ? 0 : toNumber(proj.BALANCE_REVENUE);
          const existingValue = sectorMap[sectorKey]._projectMap[projectKey].BALANCE_REVENUE === "-" ? 0 : sectorMap[sectorKey]._projectMap[projectKey].BALANCE_REVENUE;
          sectorMap[sectorKey]._projectMap[projectKey].BALANCE_REVENUE = existingValue + currentValue;
        }

        // Normalize project data
        const periods = normalizePeriodsOnly(proj, viewType);

        Object.entries(periods).forEach(([k, v]) => {
          // Add to project (aggregate if duplicate)
          sectorMap[sectorKey]._projectMap[projectKey]._periods[k] =
            (sectorMap[sectorKey]._projectMap[projectKey]._periods[k] || 0) + v;

          // Add to sector total
          sectorMap[sectorKey]._periods[k] =
            (sectorMap[sectorKey]._periods[k] || 0) + v;

          // Add to overall hold order total
          holdCombinedPeriods[k] =
            (holdCombinedPeriods[k] || 0) + v;
        });
      });
    });
  });

  // 🔹 Build Sector Rows with their projects
  const sectorChildren = Object.values(sectorMap).map((sector) => {
    const normalized = normalizeData(sector._periods, viewType);

    // Convert project map to array of project rows
    const projectRows = Object.values(sector._projectMap || {}).map((proj) => {
      const projNormalized = normalizeData(proj._periods, viewType);
      const row = {
        key: proj.key,
        label: proj.label,
        BALANCE_REVENUE: proj.BALANCE_REVENUE === "-" ? 0 : toNumber(proj.BALANCE_REVENUE),
      };

      Object.entries(projNormalized).forEach(([k, v]) => {
        row[k] = formatNumber(v);
      });

      return row;
    });

    // Calculate sector balance revenue total
    const sectorBalanceRevenueTotal = Object.values(sector._projectMap || {}).reduce((total, proj) => {
      return total + (proj.BALANCE_REVENUE === "-" ? 0 : toNumber(proj.BALANCE_REVENUE));
    }, 0);

    const sectorRow = {
      key: sector.key,
      label: sector.label,
      isSubtotal: true,
      children: projectRows,
      BALANCE_REVENUE: sectorBalanceRevenueTotal
    };

    Object.entries(normalized).forEach(([k, v]) => {
      sectorRow[k] = formatNumber(v);
    });

    return sectorRow;
  });

  // 🔹 "Other Projects" row nested below the business sectors (not above/between them)
  const { row: holdOtherProjectsRow, periods: holdOtherProjectsPeriods } =
    buildOtherProjectsSubRow(units, "hold_orders_other_projects", "HOLD_OTHER_PROJECTS", viewType);

  Object.entries(holdOtherProjectsPeriods).forEach(([k, v]) => {
    holdCombinedPeriods[k] = (holdCombinedPeriods[k] || 0) + v;
  });

  // 🔹 Hold Order Subtotal (Parent)
  const holdNormalized = normalizeData(holdCombinedPeriods, viewType);

  // Calculate total balance revenue across all sectors
  const totalBalanceRevenue = Object.values(sectorMap).reduce((sectorTotal, sector) => {
    const sectorSum = Object.values(sector._projectMap || {}).reduce((projTotal, proj) => {
      return projTotal + (proj.BALANCE_REVENUE === "-" ? 0 : toNumber(proj.BALANCE_REVENUE));
    }, 0);
    return sectorTotal + sectorSum;
  }, 0) + holdOtherProjectsRow.BALANCE_REVENUE;

  const holdRow = {
    key: "HOLD_ORDER",
    label: "Hold Orders",
    isSubtotal: true,
    children: [...sectorChildren, holdOtherProjectsRow],
    BALANCE_REVENUE: totalBalanceRevenue
  };

  Object.entries(holdNormalized).forEach(([k, v]) => {
    holdRow[k] = formatNumber(v);
  });

  return holdRow;
};

const buildAnticipatedOrderRow = (units = [], viewType) => {
  const anticipatedCombinedPeriods = {};

  const sectorMap = {}; // Use sector name as key for uniqueness

  units.forEach((unit) => {
    // Process anticipated_orders for this unit
    if (unit.anticipated_orders && unit.anticipated_orders.business_sector) {
      unit.anticipated_orders.business_sector.forEach((sector) => {
        const sectorKey = sector.BUSINESS_SECTOR;
        const sectorName = sector.BS_UNIT_NAME;

        // Create or get existing sector (merge across units)
        if (!sectorMap[sectorKey]) {
          sectorMap[sectorKey] = {
            key: `SECTOR_${sectorKey}`,
            label: sectorName,
            isSubtotal: true,
            _periods: {},
            projects: []
          };
        }

        // Process projects within this sector
        (sector.data || []).forEach((proj) => {
          const projectKey = proj.PROJECT_TITLE;

          // Check if project already exists in this sector
          if (!sectorMap[sectorKey]._projectMap) {
            sectorMap[sectorKey]._projectMap = {};
          }

          if (!sectorMap[sectorKey]._projectMap[projectKey]) {
            sectorMap[sectorKey]._projectMap[projectKey] = {
              key: `PROJ_${sectorKey}_${projectKey}`,
              // label: ` ${proj.PROJECT_TITLE} (${proj.IO_NO})`,
              label: ` ${proj.PROJECT_TITLE}`,
              _periods: {},
            };
          }

          // Normalize project data
          const periods = normalizePeriodsOnly(proj, viewType, true);

          Object.entries(periods).forEach(([k, v]) => {
            // Add to project (aggregate if duplicate)
            sectorMap[sectorKey]._projectMap[projectKey]._periods[k] =
              (sectorMap[sectorKey]._projectMap[projectKey]._periods[k] || 0) + v;

            // Add to sector total
            sectorMap[sectorKey]._periods[k] =
              (sectorMap[sectorKey]._periods[k] || 0) + v;

            // Add to overall anticipated order total
            anticipatedCombinedPeriods[k] =
              (anticipatedCombinedPeriods[k] || 0) + v;
          });

          // Note: Don't push projectRow here, it's handled in the final sector building
        });
      });
    }
  });


  // 🔹 Build Sector Rows with their projects
  const sectorChildren = Object.values(sectorMap).map((sector) => {
    const normalized = normalizeData(sector._periods, viewType, true);

    // Convert project map to array of project rows
    const projectRows = Object.values(sector._projectMap || {}).map((proj) => {
      const projNormalized = normalizeData(proj._periods, viewType, true);
      const row = {
        key: proj.key,
        label: proj.label,
        BALANCE_REVENUE: proj.BALANCE_REVENUE || 0,
      };

      Object.entries(projNormalized).forEach(([k, v]) => {
        row[k] = formatNumber(v);
      });

      return row;
    });

    // Calculate sector balance revenue total
    const sectorBalanceRevenueTotal = Object.values(sector._projectMap || {}).reduce((total, proj) => {
      return total + (proj.BALANCE_REVENUE || 0);
    }, 0);

    const sectorRow = {
      key: sector.key,
      label: sector.label,
      isSubtotal: true,
      children: projectRows,
      BALANCE_REVENUE: sectorBalanceRevenueTotal
    };

    Object.entries(normalized).forEach(([k, v]) => {
      sectorRow[k] = formatNumber(v);
    });

    return sectorRow;
  });

  // console.log("anticipatedCombinedPeriods", anticipatedCombinedPeriods);
  // 🔹 Anticipated Order Subtotal (Parent)
  const anticipatedNormalized = normalizeData(anticipatedCombinedPeriods, viewType, true);

  // Calculate total balance revenue across all sectors
  const totalBalanceRevenue = Object.values(sectorMap).reduce((sectorTotal, sector) => {
    const sectorSum = Object.values(sector._projectMap || {}).reduce((projTotal, proj) => {
      return projTotal + (proj.BALANCE_REVENUE || 0);
    }, 0);
    return sectorTotal + sectorSum;
  }, 0);

  const anticipatedRow = {
    key: "ANTICIPATED_ORDER",
    label: "Anticipated Orders",
    isSubtotal: true,
    children: sectorChildren,
    BALANCE_REVENUE: totalBalanceRevenue
  };

  Object.entries(anticipatedNormalized).forEach(([k, v]) => {
    anticipatedRow[k] = formatNumber(v);
  });

  return anticipatedRow;
};

// const buildClearOrderRow = (units = [], viewType) => {
//   const clearCombinedPeriods = {};

//   const sectorMap = {}; // Changed to use sector name as key for uniqueness

//   units.forEach((unit) => {
//     // Process clear_orders for this unit
//     (unit.clear_orders || []).forEach((sector) => {
//       const sectorKey = sector.BS_UNIT; // Use only BS_UNIT for uniqueness
//       const sectorName = sector.BS_UNIT_NAME;

//       // Create or get existing sector (merge across units)
//       if (!sectorMap[sectorKey]) {
//         sectorMap[sectorKey] = {
//           key: `SECTOR_${sectorKey}`,
//           label: sectorName,
//           isSubtotal: true,
//           _periods: {},
//           projects: []
//         };
//         // console.log("sectors", sector);
//       }

//       // Process projects within this sector
//       (sector.data || []).forEach((proj) => {
//         const projectKey = proj.PROJ_NAME;

//         if (!sectorMap[sectorKey]._projectMap) {
//           sectorMap[sectorKey]._projectMap = {};
//         }

//         if (!sectorMap[sectorKey]._projectMap[projectKey]) {
//           sectorMap[sectorKey]._projectMap[projectKey] = {
//             key: `PROJ_${sectorKey}_${projectKey}`,
//             label: `${proj.PROJ_NAME} - (${proj.IO_NO})`,
//             _periods: {},
//             _rawPeriods: {}, // ✅ always view-agnostic, for Q4 lookup
//             BALANCE_REVENUE: proj.BALANCE_REVENUE === "-" ? 0 : toNumber(proj.BALANCE_REVENUE),
//           };
//         }

//         // For display columns — filtered by viewType
//         const periodsForView = normalizePeriodsOnly(proj, viewType);

//         // ✅ For Q4 lookup — always Quarter so Q4-2025 + Jan/Feb/Mar are preserved
//         const periodsRaw = normalizePeriodsOnly(proj, "Quarter");

//         Object.entries(periodsForView).forEach(([k, v]) => {
//           sectorMap[sectorKey]._projectMap[projectKey]._periods[k] =
//             (sectorMap[sectorKey]._projectMap[projectKey]._periods[k] || 0) + v;

//           sectorMap[sectorKey]._periods[k] =
//             (sectorMap[sectorKey]._periods[k] || 0) + v;

//           clearCombinedPeriods[k] =
//             (clearCombinedPeriods[k] || 0) + v;
//         });

//         // ✅ Store raw periods for Q4 value lookup
//         Object.entries(periodsRaw).forEach(([k, v]) => {
//           sectorMap[sectorKey]._projectMap[projectKey]._rawPeriods[k] =
//             (sectorMap[sectorKey]._projectMap[projectKey]._rawPeriods[k] || 0) + v;
//         });
//       });
//     });
//   });

//   // 🔹 Build Sector Rows with their projects
//   const sectorChildren = Object.values(sectorMap).map((sector) => {
//     const normalized = normalizeData(sector._periods, viewType);

//     // Convert project map to array of project rows
//     const projectRows = Object.values(sector._projectMap || {}).map((proj) => {
//       const projNormalized = normalizeData(proj._periods, viewType);
//       const q4Key = getPreviousFYQ4Key();
//       console.log(
//         "PROJECT",
//         proj.label,
//         "Q4KEY",
//         q4Key,
//         "RAW",
//         proj[q4Key],
//         "PERIODS",
//         proj._periods?.[q4Key]
//       );
//       // const q4Value = toNumber(proj._periods[q4Key] || 0);
//       const q4Value = getQ4Value(proj._rawPeriods);
//       const balanceRevenueValue = toNumber(proj.BALANCE_REVENUE || 0);
//       const balanceRevenueDiff = balanceRevenueValue - q4Value;

//       const row = {
//         key: proj.key,
//         label: proj.label,
//         BALANCE_REVENUE: proj.BALANCE_REVENUE,
//         BALANCE_REVENUE_DIFF: balanceRevenueDiff,
//       };
//       // ADD THIS
//       Object.entries(projNormalized).forEach(([k, v]) => {
//         row[k] = formatNumber(v);
//       });

//       return row;
//     });

//     // Calculate sector balance revenue total
//     const sectorBalanceRevenueTotal = Object.values(sector._projectMap || {}).reduce((total, proj) => {
//       return total + (proj.BALANCE_REVENUE || 0);
//     }, 0);

//     // Calculate sector balance revenue difference total
//     const sectorBalanceRevenueDiffTotal = Object.values(sector._projectMap || {}).reduce((total, proj) => {
//       const projNormalized = normalizeData(proj._periods, viewType);
//       const q4Key = getPreviousFYQ4Key();
//       const q4Value = getQ4Value(proj._rawPeriods);
//       const balanceRevenueValue = toNumber(proj.BALANCE_REVENUE || 0);
//       return total + (balanceRevenueValue - q4Value);
//     }, 0);

//     const sectorRow = {
//       key: sector.key,
//       label: sector.label,
//       isSubtotal: true,
//       children: projectRows,
//       BALANCE_REVENUE: sectorBalanceRevenueTotal,
//       BALANCE_REVENUE_DIFF: sectorBalanceRevenueDiffTotal
//     };

//     Object.entries(normalized).forEach(([k, v]) => {
//       sectorRow[k] = formatNumber(v);
//     });

//     return sectorRow;
//   });
//   // 🔹 Clear Order Subtotal (Parent)
//   const clearNormalized = normalizeData(clearCombinedPeriods, viewType);

//   // Calculate total balance revenue across all sectors
//   const totalBalanceRevenue = Object.values(sectorMap).reduce((sectorTotal, sector) => {
//     const sectorSum = Object.values(sector._projectMap || {}).reduce((projTotal, proj) => {
//       return projTotal + (proj.BALANCE_REVENUE || 0);
//     }, 0);
//     return sectorTotal + sectorSum;
//   }, 0);

//   // Calculate total balance revenue difference across all sectors
//   const totalBalanceRevenueDiff = Object.values(sectorMap).reduce((sectorTotal, sector) => {
//     const sectorSum = Object.values(sector._projectMap || {}).reduce((projTotal, proj) => {

//       const projNormalized = normalizeData(proj._periods, viewType);
//       const q4Key = getPreviousFYQ4Key();
//       const q4Value = getQ4Value(proj._rawPeriods);
//       const balanceRevenueValue = toNumber(proj.BALANCE_REVENUE || 0);
//       return projTotal + (balanceRevenueValue - q4Value);
//     }, 0);
//     return sectorTotal + sectorSum;
//   }, 0);

//   const clearRow = {
//     key: "CLEAR_ORDER",
//     label: "Clear Monitoring Subtotal",
//     isSubtotal: true,
//     children: sectorChildren,
//     BALANCE_REVENUE: totalBalanceRevenue,
//     BALANCE_REVENUE_DIFF: totalBalanceRevenueDiff
//   };

//   Object.entries(clearNormalized).forEach(([k, v]) => {
//     clearRow[k] = formatNumber(v);
//   });

//   return clearRow;
// };

// Builds the non-expandable, clickable "Other Projects" row nested under a
// Clear Monitoring / Hold Order subtotal, aggregated across all units for the
// given field (clear_orders_other_projects / hold_orders_other_projects).
const buildOtherProjectsSubRow = (units = [], fieldName, rowKey, viewType) => {
  const periods = {};
  let balanceRevenue = 0;

  units.forEach((unit) => {
    const data = unit?.[fieldName];
    if (!data) return;

    const projPeriods = normalizePeriodsOnly(data, viewType);
    Object.entries(projPeriods).forEach(([k, v]) => {
      periods[k] = (periods[k] || 0) + v;
    });

    balanceRevenue += toNumber(data.BALANCE_REVENUE || 0);
  });

  const q4Total = units.reduce((sum, unit) => {
    const rawPeriods = normalizePeriodsOnly(unit?.[fieldName] || {}, "Quarter");
    return sum + getQ4Value(rawPeriods);
  }, 0);

  const normalized = normalizeData(periods, viewType);

  const row = {
    key: rowKey,
    label: "Other Projects",
    isOtherProjects: true,
    BALANCE_REVENUE: balanceRevenue,
    BALANCE_REVENUE_DIFF: balanceRevenue - q4Total,
  };

  Object.entries(normalized).forEach(([k, v]) => {
    row[k] = formatNumber(v);
  });

  return { row, periods };
};

const buildClearOrderRow = (units = [], viewType) => {
  const clearCombinedPeriods = {};

  const sectorMap = {}; // Changed to use sector name as key for uniqueness

  units.forEach((unit) => {
    // Process clear_orders for this unit
    (unit.clear_orders || []).forEach((sector) => {
      const sectorKey = sector.BS_UNIT; // Use only BS_UNIT for uniqueness
      const sectorName = sector.BS_UNIT_NAME;

      // Create or get existing sector (merge across units)
      if (!sectorMap[sectorKey]) {
        sectorMap[sectorKey] = {
          // Namespaced with CLEAR_ — hold_orders sectors reuse the same
          // BS_UNIT codes, so an unprefixed key here would collide with
          // buildHoldOrderRow's sector keys and corrupt the table's
          // expand/collapse state (rowKey="key" tracks both branches as one).
          key: `CLEAR_SECTOR_${sectorKey}`,
          label: sectorName,
          isSubtotal: true,
          _periods: {},
          projects: []
        };
        // console.log("sectors", sector);
      }

      // Process projects within this sector
      (sector.data || []).forEach((proj) => {
        const projectKey = proj.PROJ_NAME;

        if (!sectorMap[sectorKey]._projectMap) {
          sectorMap[sectorKey]._projectMap = {};
        }

        if (!sectorMap[sectorKey]._projectMap[projectKey]) {
          sectorMap[sectorKey]._projectMap[projectKey] = {
            key: `CLEAR_PROJ_${sectorKey}_${projectKey}`,
            label: `${proj.PROJ_NAME} - (${proj.IO_NO})`,
            _periods: {},
            _rawPeriods: {}, // ✅ always view-agnostic, for Q4 lookup
            BALANCE_REVENUE: proj.BALANCE_REVENUE === "-" ? 0 : toNumber(proj.BALANCE_REVENUE),
          };
        } else {
          // ✅ Club BALANCE_REVENUE across all units reporting this same project
          const currentValue = proj.BALANCE_REVENUE === "-" ? 0 : toNumber(proj.BALANCE_REVENUE);
          const existingValue = toNumber(sectorMap[sectorKey]._projectMap[projectKey].BALANCE_REVENUE);
          sectorMap[sectorKey]._projectMap[projectKey].BALANCE_REVENUE = existingValue + currentValue;
        }

        // For display columns — filtered by viewType
        const periodsForView = normalizePeriodsOnly(proj, viewType);

        // ✅ For Q4 lookup — always Quarter so Q4-2025 + Jan/Feb/Mar are preserved
        const periodsRaw = normalizePeriodsOnly(proj, "Quarter");

        Object.entries(periodsForView).forEach(([k, v]) => {
          sectorMap[sectorKey]._projectMap[projectKey]._periods[k] =
            (sectorMap[sectorKey]._projectMap[projectKey]._periods[k] || 0) + v;

          sectorMap[sectorKey]._periods[k] =
            (sectorMap[sectorKey]._periods[k] || 0) + v;

          clearCombinedPeriods[k] =
            (clearCombinedPeriods[k] || 0) + v;
        });

        // ✅ Store raw periods for Q4 value lookup
        Object.entries(periodsRaw).forEach(([k, v]) => {
          sectorMap[sectorKey]._projectMap[projectKey]._rawPeriods[k] =
            (sectorMap[sectorKey]._projectMap[projectKey]._rawPeriods[k] || 0) + v;
        });
      });
    });
  });

  // 🔹 Build Sector Rows with their projects
  const sectorChildren = Object.values(sectorMap).map((sector) => {
    const normalized = normalizeData(sector._periods, viewType);

    // Convert project map to array of project rows
    const projectRows = Object.values(sector._projectMap || {}).map((proj) => {
      const projNormalized = normalizeData(proj._periods, viewType);
      const q4Key = getPreviousFYQ4Key();
      const q4Value = getQ4Value(proj._rawPeriods);
      const balanceRevenueValue = toNumber(proj.BALANCE_REVENUE || 0);
      const balanceRevenueDiff = balanceRevenueValue - q4Value;

      const row = {
        key: proj.key,
        label: proj.label,
        BALANCE_REVENUE: proj.BALANCE_REVENUE,
        BALANCE_REVENUE_DIFF: balanceRevenueDiff,
      };
      Object.entries(projNormalized).forEach(([k, v]) => {
        row[k] = formatNumber(v);
      });

      return row;
    });

    // Calculate sector balance revenue total
    const sectorBalanceRevenueTotal = Object.values(sector._projectMap || {}).reduce((total, proj) => {
      return total + toNumber(proj.BALANCE_REVENUE || 0);
    }, 0);

    // Calculate sector balance revenue difference total
    const sectorBalanceRevenueDiffTotal = Object.values(sector._projectMap || {}).reduce((total, proj) => {
      const q4Value = getQ4Value(proj._rawPeriods);
      const balanceRevenueValue = toNumber(proj.BALANCE_REVENUE || 0);
      return total + (balanceRevenueValue - q4Value);
    }, 0);

    const sectorRow = {
      key: sector.key,
      label: sector.label,
      isSubtotal: true,
      children: projectRows,
      BALANCE_REVENUE: sectorBalanceRevenueTotal,
      BALANCE_REVENUE_DIFF: sectorBalanceRevenueDiffTotal
    };

    Object.entries(normalized).forEach(([k, v]) => {
      sectorRow[k] = formatNumber(v);
    });

    return sectorRow;
  });

  // 🔹 "Other Projects" row nested below the business sectors (not above/between them)
  const { row: clearOtherProjectsRow, periods: clearOtherProjectsPeriods } =
    buildOtherProjectsSubRow(units, "clear_orders_other_projects", "CLEAR_OTHER_PROJECTS", viewType);

  Object.entries(clearOtherProjectsPeriods).forEach(([k, v]) => {
    clearCombinedPeriods[k] = (clearCombinedPeriods[k] || 0) + v;
  });

  // 🔹 Clear Order Subtotal (Parent)
  const clearNormalized = normalizeData(clearCombinedPeriods, viewType);

  // Calculate total balance revenue across all sectors
  const totalBalanceRevenue = Object.values(sectorMap).reduce((sectorTotal, sector) => {
    const sectorSum = Object.values(sector._projectMap || {}).reduce((projTotal, proj) => {
      return projTotal + toNumber(proj.BALANCE_REVENUE || 0);
    }, 0);
    return sectorTotal + sectorSum;
  }, 0) + clearOtherProjectsRow.BALANCE_REVENUE;

  // Calculate total balance revenue difference across all sectors
  const totalBalanceRevenueDiff = Object.values(sectorMap).reduce((sectorTotal, sector) => {
    const sectorSum = Object.values(sector._projectMap || {}).reduce((projTotal, proj) => {
      const q4Value = getQ4Value(proj._rawPeriods);
      const balanceRevenueValue = toNumber(proj.BALANCE_REVENUE || 0);
      return projTotal + (balanceRevenueValue - q4Value);
    }, 0);
    return sectorTotal + sectorSum;
  }, 0) + clearOtherProjectsRow.BALANCE_REVENUE_DIFF;

  const clearRow = {
    key: "CLEAR_ORDER",
    label: "Clear Orders",
    isSubtotal: true,
    children: [...sectorChildren, clearOtherProjectsRow],
    BALANCE_REVENUE: totalBalanceRevenue,
    BALANCE_REVENUE_DIFF: totalBalanceRevenueDiff
  };

  Object.entries(clearNormalized).forEach(([k, v]) => {
    clearRow[k] = formatNumber(v);
  });

  return clearRow;
};



const buildUnitClearOrderRow = (projects, viewType) =>
  buildClearOrderRow([{ data: projects }], viewType);

const buildSummaryRow = (label, data, viewType) => {
  const norm = normalizeData1(data, viewType); //  norm here

  const row = {
    key: label,
    label,
    isSubtotal: true,
  };

  Object.entries(norm)?.forEach(([k, v]) => {
    row[k] = formatNumber(v);
  });

  return row;
};


const buildOtherProjectsRow = (data, viewType) => {
  if (!data) return null;
  const norm = normalizeData(data, viewType);

  // ✅ Q4 value view-agnostic
  const rawPeriods = normalizePeriodsOnly(data, "Quarter");
  const q4Value = getQ4Value(rawPeriods);
  const balanceRevenue = toNumber(data?.BALANCE_REVENUE || 0);

  const row = {
    key: "OTHER_PROJECTS",
    label: "Clear Other Projects",
    BALANCE_REVENUE: balanceRevenue,
    BALANCE_REVENUE_DIFF: balanceRevenue - q4Value, // ✅ correct in all views
  };

  Object.entries(norm).forEach(([k, v]) => {
    row[k] = v.toLocaleString("en-IN");
  });

  return row;
};

const buildGrandTotalRow = (rows, viewType) => {
  const combined = {};

  rows
    .filter((row) => row.isSubtotal)
    .forEach((row) => {
      Object.entries(row).forEach(([key, value]) => {
        if (
          key === "key" ||
          key === "label" ||
          key === "children" ||
          key === "isSubtotal" ||
          key === "ROW_OVERALL_TOTAL"
          // key.endsWith("__YEAR_TOTAL")
        ) {
          return;
        }

        const num = toNumber(value);

        if (!isNaN(num)) {
          combined[key] = (combined[key] || 0) + num;
        }
      });
    });

  const overallTotal = rows
    .filter((row) => row.isSubtotal)
    .reduce(
      (sum, row) => sum + toNumber(row.ROW_OVERALL_TOTAL),
      0
    );

  return {
    key: "GRAND_TOTAL",
    label: "Grand Total",
    isSubtotal: true,

    ...Object.fromEntries(
      Object.entries(combined).map(([k, v]) => [
        k,
        formatNumber(v),
      ])
    ),

    ROW_OVERALL_TOTAL: formatNumber(overallTotal),
  };
};

/* ===================== COMPONENT ===================== */

const ExecutionPlan = () => {
  const dispatch = useDispatch();
  // `state.unit.loading` is shared across many unrelated thunks throughout
  // the app (getExecutionReport itself is dispatched from three different
  // pages), so it can flip back to false the moment ANY of them resolves -
  // not specifically the fetch this table is waiting on. Track loading
  // locally instead, scoped to just this page's own fetch (mirrors
  // UnitWiseReport.jsx / IUTReport.jsx / ProjectWiseReport.jsx).
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const { executionReportDetails, allUnitsList } = useSelector((state) => state.unit);
  const { user } = useSelector((state) => state.auth);
  const { systemDate } = useSelector((state) => state.otherProjects);
  const restrictedUser = ["unit", "region"].includes(user?.role?.toLowerCase());

  const navigate = useNavigate();

  const [viewType, setViewType] = useState("Quarter");
  const [unit, setUnit] = useState("BHEL");
  const [parameter, setParameter] = useState("PROJECT_COST");

  const isBhel = unit === "BHEL";
  //  ADD THESE HELPERS (just below MONTH_ORDER or near utils)
  const fySort = (a, b) => Number(a.split("-")[0]) - Number(b.split("-")[0]);



  // const getQuarterLabel = (key) => key.split("-")[0]; // Q1-2025 => Q1
  const getQuarterLabel = (key) => key.split("-")[0];

  useEffect(() => {
    dispatch(getOtherProjectsDate("system"));
  }, [dispatch]);

  useEffect(() => {
    if (restrictedUser) {
      setUnit(sessionStorage.getItem("user_id"));
    }
  }, [restrictedUser]);
  useEffect(() => {
    // dispatch(
    //   getExecutionReport({
    //     FILTER_PARAMETER: parameter,
    //     MNF_UNIT: isBhel ? null : unit,
    //     IS_BHEL_OVERALL: isBhel,
    //   })
    // );

    const currentRequestId = ++requestIdRef.current;

    setLoading(true);
    dispatch(
      getExecutionReport({
        FILTER_PARAMETER: parameter,

        MNF_UNIT: restrictedUser
          ? sessionStorage.getItem("user_id")
          : isBhel
            ? null
            : unit,

        IS_BHEL_OVERALL: restrictedUser ? false : isBhel,
        notifyWithFilter: true,
      })
    ).finally(() => {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    });
    dispatch(getAllUnits());
  }, [dispatch, parameter, unit, isBhel]);

  /* ---------- DATA ---------- */
  const buildBhelSummaryRow = (label, units, key, viewType) => {
    const combined = {};
    let totalBalanceRevenue = 0;

    units?.forEach((u) => {
      const data = u[key];
      if (!data) return;

      const norm = normalizeData1(data, viewType); // normalize EACH unit

      // Add balance revenue from this unit's data
      if (data.BALANCE_REVENUE && data.BALANCE_REVENUE !== "-") {
        totalBalanceRevenue += toNumber(data.BALANCE_REVENUE);
      }

      Object.entries(norm).forEach(([k, v]) => {
        //  skip derived keys
        if (k.endsWith("__YEAR_TOTAL")) return;
        if (k === "ROW_OVERALL_TOTAL") return;

        combined[k] = (combined[k] || 0) + v;
      });
    });

    // normalize ONCE at the end
    const finalNorm = normalizeData1(combined, viewType);

    const row = {
      key: label,
      label,
      isSubtotal: true,
      BALANCE_REVENUE: totalBalanceRevenue
    };

    Object.entries(finalNorm)?.forEach(([k, v]) => {
      if (k !== "BALANCE_REVENUE") {
        row[k] = formatNumber(v);
      }
    });

    return row;
  };


  const tableData = useMemo(() => {
    if (!executionReportDetails) return [];

    const rows = [
      buildClearOrderRow(
        executionReportDetails.units || [],
        viewType
      ),
      // Disabled per request — top-level "Other Projects" row no longer renders
      // buildBhelOtherProjectsRow(
      //   executionReportDetails.units,
      //   viewType
      // ),
      buildBhelSummaryRow(
        "Direct Order",
        executionReportDetails.units,
        "direct_orders",
        viewType
      ),
      buildAnticipatedOrderRow(
        executionReportDetails.units || [],
        viewType
      ),

      buildHoldOrderRow(
        executionReportDetails.units || [],
        viewType
      ),
    ].filter(Boolean);

    const grandTotalRow = buildGrandTotalRow(rows, viewType);

    return [grandTotalRow, ...rows];

  }, [executionReportDetails, viewType, isBhel]);

  /* ---------- FLATTEN ---------- */

  const rows = useMemo(() => {
    const flat = [];
    const walk = (r) => {
      flat.push(r);
      r.children?.forEach(walk);
    };
    tableData?.forEach(walk);
    return flat;
  }, [tableData]);

  /* ---------- EXPAND ALL / COLLAPSE ALL ---------- */

  // Only rows carrying a non-empty `children` array can ever be expanded —
  // the Grand Total row is also flagged isSubtotal but has no children of
  // its own, so it's correctly excluded here.
  const expandableRowKeys = useMemo(
    () => rows.filter((row) => Array.isArray(row.children) && row.children.length > 0).map((row) => row.key),
    [rows]
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const isAllExpanded =
    expandableRowKeys.length > 0 &&
    expandableRowKeys.every((key) => expandedRowKeys.includes(key));

  const handleToggleExpandAll = () => {
    setExpandedRowKeys(isAllExpanded ? [] : expandableRowKeys);
  };

  const groupedKeys = useMemo(() => {
    const map = {};
    const prevQ4Key = getPreviousFYQ4Key(); // Q4-2025 — allowed as standalone column only

    if (!rows || !Array.isArray(rows)) return map;

    rows.forEach((r) => {
      if (!r || typeof r !== "object") return;

      Object.keys(r).forEach((k) => {
        const year = getYearFromKey(k, viewType);
        if (!year) return;

        const valid =
          (viewType === "Year" && /^\d{4}-\d{4}$/.test(k)) ||
          (viewType === "Quarter" && /^Q[1-4]-\d{4}$/.test(k)) ||
          (viewType === "Month" &&
            /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(k));

        if (!valid) return;

        const currentFYStartYear = getCurrentFYStartYear(); // 2026
        const yearStart = /^\d{4}-\d{4}$/.test(year)
          ? Number(year.split("-")[0])
          : Number(year);

        // ✅ Only allow prevQ4 as a standalone key — do NOT add it to map (no column group)
        if (k === prevQ4Key) return;

        // ✅ Filter out anything before current FY (2026-27)
        if (yearStart < currentFYStartYear) return;

        if (!map[year]) map[year] = new Set();
        map[year].add(k);
      });
    });

    Object.keys(map).forEach((y) => {
      if (!map[y]) return;
      map[y] = Array.from(map[y]).sort((a, b) => {
        if (viewType === "Month") {
          return (
            MONTH_ORDER.indexOf(a.split("-")[0]) -
            MONTH_ORDER.indexOf(b.split("-")[0])
          );
        }
        return a.localeCompare(b);
      });
    });

    return map;
  }, [rows, viewType]);

  const firstQuarter =
    viewType === "Quarter"
      ? getPreviousFYQ4Key()
      : null;

  const firstQuarterColumn =
    firstQuarter && viewType === "Quarter"
      ? {
        // title: `Q4 / ${getFYShortLabel(firstQuarter.split("-")[1])}`,
        title: `Q4/${getFYShortLabel(Number(firstQuarter.split("-")[1]))}`,
        dataIndex: firstQuarter,
        align: "center",
        width: 140,
        render: (val) => formatNumber(val),
        onHeaderCell: () => ({ style: headerStyle }),
      }
      : null;

  const openOtherProjects = (isClear) => {
    const params = new URLSearchParams({
      unit,
      viewType,
      parameter,
    });

    if (isClear !== undefined) {
      params.set("IS_CLEAR", isClear);
    }

    window.open(
      `${PATHNAME.ADMIN_CONSOLIDATED_OTHER_PROJECTS_EXECUTION_PLAN}?${params.toString()}`,
      "_blank"
    );
  };
  /* ---------- COLUMNS ---------- */
  //  REPLACE ONLY THIS columns useMemo (your existing one)
  const columns = useMemo(() => {
    // const cols = [baseColumn, balanceRevenueColumn, balanceRevenueDifferenceColumn];
    const cols = [baseColumn];
    cols[0] = {
      ...baseColumn,
      render: (text, record) => {
        if (record.key === "GRAND_TOTAL") {
          return (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                aria-label={isAllExpanded ? "Collapse All" : "Expand All"}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpandAll();
                }}
                className={`ant-table-row-expand-icon ant-table-row-expand-icon-${isAllExpanded ? "expanded" : "collapsed"}`}
                style={{ flexShrink: 0 }}
              />
              <strong>{text}</strong>
            </span>
          );
        }

        // Disabled per request — top-level "Other Projects" row no longer renders
        // if (record.key === "Other Projects") {
        //   return (
        //     <strong
        //       style={{
        //         color: "#1677ff",
        //         cursor: "pointer",
        //         textDecoration: "underline",
        //       }}
        //       onClick={() => openOtherProjects()}
        //     >
        //       {text}
        //     </strong>
        //   );
        // }

        if (record.key === "CLEAR_OTHER_PROJECTS") {
          return (
            <strong
              style={{
                color: "#1677ff",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => openOtherProjects(1)}
            >
              {text}
            </strong>
          );
        }

        if (record.key === "HOLD_OTHER_PROJECTS") {
          return (
            <strong
              style={{
                color: "#1677ff",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => openOtherProjects(0)}
            >
              {text}
            </strong>
          );
        }

        return <strong>{text}</strong>;
      },
    };

    if (parameter === "REVENUE") {
      const dynamicBalanceTitle = systemDate?.STARTDATE
        ? `Order as on ${systemDate.STARTDATE}`
        : getBalanceTitle();

      const dynamicBalanceRevenueColumn = {
        ...balanceRevenueColumn,
        title: dynamicBalanceTitle,
      };

      // 1. Order O/s / Order as on (system date) comes first
      cols.push(dynamicBalanceRevenueColumn);

      // Note: Q4-25-26 (firstQuarterColumn) and Balance Revenue Difference (balanceRevenueDifferenceColumn)
      // are hidden under REVENUE filter as requested.
    }

    const firstQuarterYear = firstQuarter
      ? `${firstQuarter.split("-")[1]}-${Number(firstQuarter.split("-")[1]) + 1}`
      : null;
    const groups = Object.keys(groupedKeys)
      .filter((year) => year !== firstQuarterYear)
      .sort((a, b) => {
        if (viewType === "Quarter") return fySort(a, b);
        if (viewType === "Year") return fySort(a, b);
        return Number(a) - Number(b);
      });

    groups.forEach((year, idx) => {
      let keys = groupedKeys[year] || [];
      // if (viewType === "Quarter" && firstQuarter) {
      //   keys = keys.filter((k) => k !== firstQuarter);
      // }

      if (viewType === "Quarter") {

        const currentFY = `${getCurrentFYStartYear()}-${getCurrentFYStartYear() + 1}`;

        if (year === currentFY) {
          keys = keys.filter((k) => !k.startsWith("Q4-"));
        }

        if (firstQuarter) {
          keys = keys.filter((k) => k !== firstQuarter);
        }
      }
      if (viewType === "Year") {
        // FLAT columns (NO parent, NO children)
        keys.forEach((k) => {
          cols.push({
            title: k,
            dataIndex: k,
            align: "center",
            width: 140,
            render: (val) => formatNumber(val),
            onHeaderCell: () => ({ style: headerStyle }),
          });
        });
      } else {
        //  Grouped columns for Quarter / Month
        cols.push({
          title: headerFYLabel(year), //  Quarter heading becomes FY like 2024-2025
          align: "center",
          onHeaderCell: () => ({ style: headerStyle }),
          children: [
            ...keys.map((k, i) => ({
              //  Quarter child heading becomes Q1/Q2/Q3/Q4
              title: viewType === "Quarter" ? getQuarterLabel(k) : k,
              dataIndex: k,
              align: "center",
              width: 140,
              render: (val) => formatNumber(val),
              //  avoid duplicate column key warnings (same Q1 repeats across years)
              key: `${year}_${k}_${i}`,
            })),
            // buildYearTotalColumn(year),
            // Skip total if this group had first quarter
            ...(viewType === "Quarter" && year !== firstQuarterYear
              ? [buildYearTotalColumn(year)]
              : [])
          ],
        });
      }
    });

    cols.push(overallColumn);
    return cols;
  }, [groupedKeys, viewType, parameter, firstQuarterColumn, systemDate, isAllExpanded, handleToggleExpandAll]);

  /* ===================== RENDER ===================== */

  return (
    <div className="p-5">
      <div className="flex items-center gap-3">
        <div className="flex gap-3 items-center">
          <img src={ActiveProject} alt="region" />
          <div>
            <div className="font-inter font-semibold text-[24px] text-[#101828]">
              Execution Plan - Unitwise
            </div>
            <div className="text-gray-500 text-sm">
              * All figures are in Rupees (in Crores)
            </div>

          </div>
        </div>


      </div>

      <div className="mt-6 bg-white shadow-lg rounded-xl p-4">
        <div className="flex justify-between mb-4">
          <div>

          </div>

          <div className="flex gap-3">
            {!restrictedUser && (
              <CustomSelectWithFilter
                data={[
                  { label: "BHEL Overall", value: "BHEL" },
                  ...(allUnitsList || []).map((u) => ({
                    label: u.UNIT_DISPLAY_NAME,
                    value: u.MNF_UNIT,
                  })),
                ]}
                placeholder="BHEL Overall"
                onChange={setUnit}
                className="w-[14rem]"
              />
            )}

            <Select
              value={viewType}
              onChange={setViewType}
              style={{ width: 160 }}
            >
              <Option value="Year">Year Wise</Option>
              <Option value="Quarter">Quarter Wise</Option>
              <Option value="Month">Month Wise</Option>
            </Select>

            <CustomSelectWithFilter
              data={[
                { label: "Revenue", value: "REVENUE" },
                { label: "Project cost", value: "PROJECT_COST" },
                { label: "Profit/Loss", value: "PROFIT_LOSS" },
                { label: "Apportioned cost", value: "APPORTIONED_COST" },
                { label: "Operational income", value: "OPERATIONAL_INCOME" },
                { label: "Project profit", value: "PROJECT_PROFIT" },
                { label: "Net billing", value: "NET_BILLING" },
                { label: "Cash inflow", value: "CASH_INFLOW" },
                { label: "Cash outflow", value: "CASH_OUTFLOW" },
                { label: "Net cash flow", value: "NET_CASH_FLOW" }
              ]}
              onChange={setParameter}
              placeholder="PROJECT_COST"
              className="w-[14rem]"
            />
          </div>
        </div>

        <Table
          bordered
          loading={loading}
          pagination={false}
          scroll={{ x: "max-content", y: 520 }}
          className="execution-plan-table"
          expandable={{
            // Original predicate flagged the Grand Total row as expandable too
            // (it also carries isSubtotal), even though it has no children of
            // its own — checking for a real `children` array is the accurate
            // signal and lines up with the Expand All toggle below.
            // rowExpandable: (row) => row.isSubtotal || row.isBusinessSector || (row.isUnit && row.children && row.children.length > 0),
            rowExpandable: (row) => Array.isArray(row.children) && row.children.length > 0,
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
          }}
          rowClassName={(row) =>
            row.isSubtotal ? "bg-blue-50 font-semibold" :
              row.isBusinessSector ? "bg-orange-50 font-semibold" :
                row.isUnit ? "bg-green-50 font-semibold" : ""
          }
          columns={columns}
          dataSource={tableData}
          rowKey="key"
        />
        {/* {console.log(tableData)} */}
      </div>
    </div>
  );
};

export default ExecutionPlan;
